import { Icon, Text } from '@rbilabs/universal-components';
import { useProductWizardContext } from 'features/menu/components/product-wizard';
import { SlotsSelections } from 'features/menu/components/product-wizard/types';
import { computeComboSodium } from 'features/menu/components/product-wizard/utils';
import { StyleSheet, View } from 'react-native';

import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { ILocalizedMenuEntry, IndexedMenu } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { primitive, spacing } from 'styles/constants';
import { Region } from 'utils/environment/types';

const SODIUM_LIMIT = 2300;

function hasExcessSodium(
  menu: IndexedMenu<ILocalizedMenuEntry>,
  selectedProduct: ILocalizedMenuEntry,
  slotsSelections: SlotsSelections
) {
  let totalSodium = 0;

  if (selectedProduct.type === 'ITEM') {
    totalSodium = selectedProduct.nutrition?.nutrients?.sodium ?? 0;
  } else if (selectedProduct.type === 'COMBO') {
    totalSodium = computeComboSodium(menu, selectedProduct, slotsSelections);
  }

  return totalSodium > SODIUM_LIMIT;
}

export const OverSodiumLimitWarning = () => {
  const { region } = useLocale();
  const { indexedMenu } = useUnifiedMenuContext();
  const { selectedProduct, slotsSelections } = useProductWizardContext();
  const enableSodiumWarning = useFlag(LaunchDarklyFlag.ENABLE_SODIUM_WARNING);

  if (!enableSodiumWarning || region !== Region.US) {
    return null;
  }

  if (!hasExcessSodium(indexedMenu, selectedProduct, slotsSelections)) {
    return null;
  }

  return (
    <View style={styles.container}>
      <Icon variant="sodiumFlag" testID="sodium-flag" size={{ base: '18px', md: '24px' }} />
      <Text fontSize={{ base: primitive.fontSizes.lg, md: primitive.fontSizes['2xl'] }}>
        {t('sodiumWarning', 'Sodium Warning').toUpperCase()}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: spacing.$4,
    gap: 4,
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
});
